import { initializeApp, getApps, type FirebaseApp } from 'firebase/app';
import { initializeAppCheck, ReCaptchaV3Provider, type AppCheck } from 'firebase/app-check';

import { getFirebaseEnvConfig } from './config';

type FirebaseGlobalState = typeof globalThis & {
  __verdeconfirmaFirebaseApp?: FirebaseApp;
  __verdeconfirmaFirebaseAppCheck?: AppCheck;
};

const firebaseGlobal = globalThis as FirebaseGlobalState;

function getFirebaseConfig() {
  const config = getFirebaseEnvConfig();

  if (
    !config.apiKey ||
    !config.authDomain ||
    !config.projectId ||
    !config.storageBucket ||
    !config.messagingSenderId ||
    !config.appId
  ) {
    throw new Error('Firebase Web SDK is not configured. Check the VITE_FIREBASE_* environment variables.');
  }

  return config;
}

function ensureAppCheck(app: FirebaseApp): void {
  if (firebaseGlobal.__verdeconfirmaFirebaseAppCheck) {
    return;
  }

  const config = getFirebaseEnvConfig();
  const useDebugAppCheck = import.meta.env.DEV || config.useEmulators;

  if (useDebugAppCheck) {
    (globalThis as typeof globalThis & { FIREBASE_APPCHECK_DEBUG_TOKEN?: boolean }).FIREBASE_APPCHECK_DEBUG_TOKEN = true;
  }

  if (!useDebugAppCheck && !config.appCheckSiteKey) {
    throw new Error('VITE_FIREBASE_APPCHECK_SITE_KEY is required outside development or emulator mode.');
  }

  firebaseGlobal.__verdeconfirmaFirebaseAppCheck = initializeAppCheck(app, {
    provider: new ReCaptchaV3Provider(config.appCheckSiteKey || 'debug-site-key'),
    isTokenAutoRefreshEnabled: true,
  });
}

export function getFirebaseApp(): FirebaseApp {
  if (firebaseGlobal.__verdeconfirmaFirebaseApp) {
    return firebaseGlobal.__verdeconfirmaFirebaseApp;
  }

  const apps = getApps();

  if (apps.length > 0) {
    firebaseGlobal.__verdeconfirmaFirebaseApp = apps[0]!;
    ensureAppCheck(firebaseGlobal.__verdeconfirmaFirebaseApp);
    return firebaseGlobal.__verdeconfirmaFirebaseApp;
  }

  const config = getFirebaseConfig();
  const app = initializeApp({
    apiKey: config.apiKey,
    authDomain: config.authDomain,
    projectId: config.projectId,
    storageBucket: config.storageBucket,
    messagingSenderId: config.messagingSenderId,
    appId: config.appId,
    measurementId: config.measurementId || undefined,
  });

  firebaseGlobal.__verdeconfirmaFirebaseApp = app;
  ensureAppCheck(app);

  return app;
}
